/**
 * All types a group can be
 */
export const GROUP_TYPES = {
  TEAM: 'team',
  DEFAULT: 'default',
  CLINIC: 'clinic',
  DEPARTMENT: 'department',
  FACILITY: 'facility',
  NETWORK: 'network',
  REGION: 'region',
  SERVICE_LINE: 'service-line',
  UNIT: 'unit',
  // An author's reviewer list: no roles, no OpenFGA tuples, not part of the org hierarchy.
  REVIEWER: 'reviewer'
} as const;

/**
 * The names of all group types
 */
export const GROUP_NAMES = {
  [GROUP_TYPES.TEAM]: 'Team',
  [GROUP_TYPES.DEFAULT]: 'Organization Default',
  [GROUP_TYPES.CLINIC]: 'Clinic',
  [GROUP_TYPES.DEPARTMENT]: 'Department',
  [GROUP_TYPES.FACILITY]: 'Facility',
  [GROUP_TYPES.NETWORK]: 'Network',
  [GROUP_TYPES.REGION]: 'Region',
  [GROUP_TYPES.SERVICE_LINE]: 'Service Line',
  [GROUP_TYPES.UNIT]: 'Unit',
  [GROUP_TYPES.REVIEWER]: 'Reviewer Group'
} as const;

/**
 * Notification categories a support contact can subscribe to, in display order. Single-sourced
 * here because admin console, web-hq and the maintenance emailer each render or query them, and
 * hand-kept copies drift. `field` is the model attribute; snake_cased, it is the DB column.
 * `usage` and `inclusion` are the customer-facing guidance shown in admin console.
 */
export const SUPPORT_CONTACT_TYPES = [
  {
    field: 'agilemdPlannedMaintenanceNoDowntime' as const,
    label: 'Planned Maintenance (No Downtime)',
    shortLabel: 'Planned (No DT)',
    usage:
      'Used for advance notice of routine changes that do not interrupt customer access, such as enhancements, bug fixes, or system updates.',
    inclusion:
      'Include email contacts who should be informed of planned maintenance, such as application owners, clinical informatics leads, program leads, operational stakeholders, and targeted/applicable distribution lists.'
  },
  {
    field: 'agilemdPlannedMaintenanceWithDowntime' as const,
    label: 'Planned Maintenance (With Downtime)',
    shortLabel: 'Planned (With DT)',
    usage:
      'Used for advance notice of scheduled production downtime where customer impact is expected.',
    inclusion:
      'In addition to audiences included on the Planned Maintenance (No Downtime) list, consider including email contacts responsible for operational readiness and internal communication, such as help desk leadership, EHR support, application administrators, HL7/integration support, networking support, change management contacts, business owners, and program leads.'
  },
  {
    field: 'agilemdUnplannedDowntime' as const,
    label: 'Unplanned Downtime',
    shortLabel: 'Unplanned DT',
    usage:
      'Used when AgileMD needs to notify the customer of an unexpected production outage or service interruption.',
    inclusion:
      'Include email contacts who should be notified immediately, such as IT operations, command center or service desk distribution lists, application support teams, clinical operations, and operational leaders.'
  },
  {
    field: 'agilemdUnplannedDowntimeFollowUp' as const,
    label: 'Unplanned Downtime Follow-Up',
    shortLabel: 'DT Follow-Up',
    usage:
      'Used for the on-call team responsible for active follow-up during an unplanned production issue.',
    inclusion:
      'Include email contacts responsible for response, investigation, and resolution coordination, such as on-call analysts, interface engineers, technical owners, HL7 or integration support, networking support, and monitored escalation distribution lists.'
  },
  {
    field: 'customerIncident' as const,
    label: 'Customer Incident',
    shortLabel: 'Cust. Incident',
    usage:
      'Used when AgileMD detects a customer outage or production incident, such as VPN, networking, data interface, HL7, or web services issues.',
    inclusion:
      'Include the Help Desk contact and the customer teams responsible for ticket routing, intake, investigation, and escalation, such as network, interface, HL7 or integration support, EHR support, infrastructure, and command center contacts.'
  },
  {
    field: 'agilemdReportingFeedIncident' as const,
    label: 'Outbound Reporting Feed',
    shortLabel: 'Reporting Feed',
    usage:
      'Used for notifications specific to the SFTP reporting data, including updated specifications, maintenance, backfills, planned outages, and unplanned outages.',
    inclusion:
      'Include email contacts who own, support, or monitor the feed, such as data engineering, interface teams, analytics platform owners, SFTP administrators, and reporting or BI support teams.'
  },
  {
    field: 'sso' as const,
    label: 'Single Sign-On',
    shortLabel: 'SSO',
    usage:
      'If applicable to your organization, used for notifications related to Single Sign-On (SSO) maintenance or issues. If not applicable, please leave blank.',
    inclusion: 'Include email contacts who support SSO such as Identity/Access technical contacts.'
  }
] as const;

export type SupportContactType = (typeof SUPPORT_CONTACT_TYPES)[number]['field'];

/**
 * Per-organization capability switches. The key is the model attribute; snake_cased, it is the DB
 * column.
 */
export const ORGANIZATION_FEATURES = {
  AI_ENABLED_FOR_CONTENT: 'aiEnabledForContent',
  AI_ENABLED_FOR_PHI: 'aiEnabledForPhi',
  EXTERNAL_DATA_WAREHOUSE_ENABLED: 'externalDataWarehouseEnabled'
} as const;

export type OrganizationFeature =
  (typeof ORGANIZATION_FEATURES)[keyof typeof ORGANIZATION_FEATURES];

/**
 * How each feature's column stores its state. The AI switches keep the date they were turned on,
 * since consent matters from a point in time; the warehouse flag is a plain boolean.
 */
export const ORGANIZATION_FEATURE_STORAGE = {
  [ORGANIZATION_FEATURES.AI_ENABLED_FOR_CONTENT]: 'timestamp',
  [ORGANIZATION_FEATURES.AI_ENABLED_FOR_PHI]: 'timestamp',
  [ORGANIZATION_FEATURES.EXTERNAL_DATA_WAREHOUSE_ENABLED]: 'boolean'
} as const satisfies Record<OrganizationFeature, 'timestamp' | 'boolean'>;

/** Organization content every pathway overview footer links to; published from the Admin Console. */
export const ORGANIZATION_PATHWAY_SETTING_KINDS = {
  LEGEND: 'legend',
  DISCLAIMER: 'disclaimer',
  CUSTOM_FOOTER: 'customFooter'
} as const;
export type OrganizationPathwaySettingKind =
  (typeof ORGANIZATION_PATHWAY_SETTING_KINDS)[keyof typeof ORGANIZATION_PATHWAY_SETTING_KINDS];

export const ORGANIZATION_PATHWAY_SETTING_LABELS = {
  [ORGANIZATION_PATHWAY_SETTING_KINDS.LEGEND]: 'Legend',
  [ORGANIZATION_PATHWAY_SETTING_KINDS.DISCLAIMER]: 'Disclaimer',
  [ORGANIZATION_PATHWAY_SETTING_KINDS.CUSTOM_FOOTER]: 'Custom footer item'
} as const satisfies Record<OrganizationPathwaySettingKind, string>;

/** The footer link icon presets; the empty key is no icon. */
export const CUSTOM_FOOTER_ICONS = [
  { key: '', label: 'No icon' },
  { key: 'info', label: 'Info' },
  { key: 'hospital', label: 'Organization' },
  { key: 'users', label: 'Team' },
  { key: 'program', label: 'Program' },
  { key: 'comment', label: 'Feedback' },
  { key: 'link', label: 'Link' }
] as const;
export type CustomFooterIconKey = (typeof CUSTOM_FOOTER_ICONS)[number]['key'];

/** Raster types and size cap for an uploaded footer logo; enforced by the server and the Admin Console. */
export const UPLOAD_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp'
] as const;

export const UPLOAD_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/** Where `@agilemd/cdn-client` stores uploads (production, then non-production). */
export const UPLOAD_CDN_ORIGINS = {
  PRODUCTION: 'https://d39apiqqtswgrh.cloudfront.net',
  NON_PRODUCTION: 'https://dvji26ozuyuzi.cloudfront.net'
} as const;

/** Longest footer link title, so the footer never wraps. */
export const CUSTOM_FOOTER_TITLE_MAX_LENGTH = 24;

export const ORGANIZATION_PATHWAY_SETTING_EVENT_TYPES = {
  MANUAL_EDIT: 'manual_edit',
  PROMOTE: 'promote',
  RESTORE: 'restore'
} as const;
export type OrganizationPathwaySettingEventType =
  (typeof ORGANIZATION_PATHWAY_SETTING_EVENT_TYPES)[keyof typeof ORGANIZATION_PATHWAY_SETTING_EVENT_TYPES];
